import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import api, { resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

export default function ImageUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid="image-uploader">
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder="https://… or upload below"
        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm"
        data-testid="image-url-input"
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-cream border border-ink/10 hover:border-brand/40 text-sm text-ink-muted disabled:opacity-60"
          data-testid="upload-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Uploading…" : "Upload image"}
        </button>
        <span className="text-xs text-gray-400">PNG/JPG/WEBP, up to 8 MB</span>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>
      {value ? (
        <div className="relative inline-block mt-1">
          <img src={resolveImageUrl(value)} alt="Cover" className="max-h-40 rounded-lg border border-ink/5" />
          <button
            type="button"
            onClick={() => onChange?.("")}
            className="absolute -top-2 -right-2 bg-white border border-ink/10 rounded-full p-1 shadow-sm hover:bg-gray-50"
            title="Remove"
            data-testid="remove-image-btn"
          >
            <X className="w-3.5 h-3.5 text-gray-600" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
